import { jsx as _jsx } from "react/jsx-runtime";
import { isEmpty } from "../../utils/nativeUtils";
import { useContext } from "react";
import { injectIntl } from "react-intl";
import { WithSearch } from "@elastic/react-search-ui";
import { i18nConfig } from "../../i18nConfig";
import config from "../../config";
import { FilterOptionEnum, FilterTypeEnum } from "../../models/dataModel";
import { UIStateStoreContext } from "../../stores/uiStateStore";
const Tag = injectIntl(({ intl, tag }) => {
    function translateNotVersionSpecificLabel(label) {
        return label === i18nConfig.messages[intl.defaultLocale]["facets.software_version.notVersionSpecific"]
            ? (!!i18nConfig.messages[intl.locale] &&
                i18nConfig.messages[intl.locale]["facets.software_version.notVersionSpecific"]) ||
                i18nConfig.messages[intl.defaultLocale]["facets.software_version.notVersionSpecific"]
            : label;
    }
    const label = !!tag.label &&
        (typeof tag.label === "string"
            ? tag.label.substring(0, 1).toUpperCase() + tag.label.substring(1)
            : Array.isArray(tag.label)
                ? translateNotVersionSpecificLabel(tag.label[0])
                : tag.label);
    return (_jsx(WithSearch, { mapContextToProps: ({ addFilter }) => ({ addFilter }), children: ({ addFilter }) => {
            function handleClick() {
                tag.filters.forEach((filter) => addFilter(filter.field, filter.value, filter.type));
            }
            function handleKeyDown(event) {
                if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    handleClick();
                }
            }
            return (_jsx("div", { className: "result-tag", "data-testid": "tag", onClick: handleClick, tabIndex: 0, "aria-label": `Add ${tag.filters[0].field} filter: ${tag.label}`, onKeyDown: handleKeyDown, children: label || tag.filters[0].value }));
        } }));
});
export const Tags = ({ result }) => {
    const uiStore = useContext(UIStateStoreContext);
    const showCategoryTags = uiStore.inGlobalSearchMode() ? !!uiStore.getOpenCategory() : true;
    const tagList = generateTags();
    function generateTags() {
        let tags = [];
        if (showCategoryTags) {
            (config.services[uiStore.getService()].tagsToDisplay || []).forEach((tagToDisplay) => {
                if (tagToDisplay === FilterOptionEnum.PRODUCT) {
                    tags = generateProductTags(result);
                }
                else if (tagToDisplay === FilterOptionEnum.VERSION) {
                    tags = generateVersionTags(result, tags);
                }
                else {
                    generateTagsForFacet(tagToDisplay, tags);
                }
            });
        }
        // Add category as the first tag in global search mode
        if (uiStore.inGlobalSearchMode() &&
            result[FilterOptionEnum.CATEGORY] &&
            !isEmpty(result[FilterOptionEnum.CATEGORY].raw)) {
            tags.unshift({
                label: result[FilterOptionEnum.CATEGORY].raw,
                filters: [
                    {
                        field: FilterOptionEnum.CATEGORY,
                        value: result[FilterOptionEnum.CATEGORY].raw,
                        type: FilterTypeEnum.ALL,
                    },
                ],
            });
        }
        return tags;
    }
    function generateProductTags(result) {
        const productTags = [];
        if (result.product && !isEmpty(result.product.raw)) {
            const products = typeof result.product.raw === "string"
                ? result.product.raw.split(",")
                : result.product.raw;
            if (products.length > 1) {
                products.forEach((product) => {
                    productTags.push({
                        filters: [
                            {
                                field: FilterOptionEnum.PRODUCT,
                                value: product,
                                type: FilterTypeEnum.ALL,
                            },
                        ],
                    });
                });
            }
            else {
                productTags.push({
                    filters: [
                        {
                            field: FilterOptionEnum.PRODUCT,
                            value: products[0],
                            type: FilterTypeEnum.ALL,
                        },
                    ],
                });
            }
        }
        return productTags;
    }
    function generateVersionTags(result, tags) {
        if (!(tags.length > 1) && result.software_version && !isEmpty(result.software_version.raw)) {
            let versions = typeof result.software_version.raw === "string"
                ? result.software_version.raw.split(",")
                : result.software_version.raw;
            let notSpecificVersionTag;
            const versionFilters = versions.map((version) => {
                return {
                    field: FilterOptionEnum.VERSION,
                    value: version,
                    type: FilterTypeEnum.ANY,
                };
            });
            if (!isEmpty(tags)) {
                tags = tags.map((tag) => {
                    const products = tag.filters;
                    let label = "";
                    if (tag.filters === undefined || isEmpty(tag.filters))
                        return tag;
                    products.forEach((productFilter) => {
                        const product = productFilter.value;
                        // If product is not one of listed Tekla products, filter versions to include only those that match the product name or its variant without "Tekla"
                        // if (!["Tekla Structures", "Tekla EPM", "Tekla Structural Designer", "Tekla Tedds"].includes(product)) {
                        //   const productWithoutTekla: string = product.includes("Tekla") ? product.replace("Tekla ", "") : "";
                        //   versions = versions.filter((v: string) => v.includes(product) || v.includes(productWithoutTekla));
                        // }
                        versions = versions
                            .map((version) => {
                            // If version and product are same, return version as is, else return only the last part after space (assuming it's the version number)
                            if (version == product) {
                                return version;
                            }
                            else {
                                return version.split(" ").pop() || "";
                            }
                        })
                            .sort();
                        // If there are multiple versions, show the first and last version only (e.g. Tekla Structures 2021 - 2023)
                        if (versions.length > 1) {
                            label = `${product}  ${versions.slice(0, 1)} - ${versions.pop()}`;
                        }
                        else if (versions.length === 1) {
                            // If the only version is "Not version-specific", show only the product name as label but add the version as a separate tag
                            if (result.software_version.raw &&
                                result.software_version.raw[0].toLowerCase() === "not version-specific") {
                                label = product;
                                notSpecificVersionTag = result.software_version.raw;
                                // If the only version is same as product name, show only the product name as label
                            }
                            else if (versions[0] === product)
                                label = product;
                            // Otherwise, show product name and version
                            else {
                                label = `${product}  ${versions}`;
                            }
                        }
                        else {
                            label = product;
                        }
                    });
                    return {
                        label: label,
                        filters: products.concat(versionFilters),
                    };
                });
                if (notSpecificVersionTag) {
                    tags.push({
                        label: notSpecificVersionTag,
                        filters: [{ field: FilterOptionEnum.VERSION, value: notSpecificVersionTag, type: FilterTypeEnum.ANY }],
                    });
                }
            }
            else {
                versionFilters.forEach((version) => {
                    tags.push({
                        filters: [
                            {
                                field: FilterOptionEnum.VERSION,
                                value: version.value,
                                type: FilterTypeEnum.ANY,
                            },
                        ],
                    });
                });
            }
        }
        return tags;
    }
    /**
     * Generates tags for a given facet and adds them to the tags array
     * Facet is looked up from config to get its filter type, label, etc.
     * Facets are only generated if the result has values for the facet
     * If the facet value is an array, a tag is created for each value
     *
     * @param facetName - name of the facet (should match FilterOptionEnum)
     * @param tags - array to which generated tags will be added
     */
    function generateTagsForFacet(facetName, tags) {
        const facet = (config.services[uiStore.getService()].facetList || []).find(f => f.field === facetName);
        if (result[facetName] && !isEmpty(result[facetName].raw)) {
            if (Array.isArray(result[facetName].raw)) {
                result[facetName].raw.forEach((value) => {
                    tags.push({
                        label: value,
                        filters: [
                            {
                                field: facet.field,
                                value: value,
                                type: facet.filterType,
                            }
                        ]
                    });
                });
            }
            else {
                tags.push({
                    label: result[facet.field].raw,
                    filters: [
                        {
                            field: facet.field,
                            value: result[facet.field].raw,
                            type: facet.filterType,
                        }
                    ]
                });
            }
        }
    }
    return (_jsx("div", { className: "tag-list", "data-testid": "result-tags", children: !isEmpty(tagList) &&
            tagList.map((tag, i) => {
                return _jsx(Tag, { tag: tag }, i);
            }) }));
};
